import { supabase } from "./index";
import { WordImageUrlPair, WordType } from "../../features/words/types";
import { WordImage, WordService, WordServiceOptions } from "../WordService";

export class SupabaseWordService extends WordService {
  static readonly IMAGE_BASE_URL = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/images/`;
  static readonly SUPABASE_MAX_PAGE_SIZE = 50;

  async getWordsWhereFieldEquals(
    field: string,
    value: string,
    options?: WordServiceOptions
  ): Promise<WordType[]> {
    let query = supabase
      .from("words_fr")
      .select()
      .eq(field, value)
      .order(field, { ascending: true })
      .limit(Math.max(options?.limit ?? 0, 1));

    const { data, error } = await query;

    if (error) {
      throw new Error(error.message);
    }

    return data as WordType[];
  }

  async getWordsWhereFieldLike(
    field: string,
    value: string,
    options?: WordServiceOptions
  ): Promise<WordType[]> {
    let query = supabase
      .from("words_fr")
      .select()
      .like(field, `${value}%`)
      .order(field, { ascending: true })
      .limit(Math.max(options?.limit ?? 0, 1));

    const { data, error } = await query;

    if (error) {
      throw new Error(error.message);
    }

    return data as WordType[];
  }

  async getImagesForWord(word: string): Promise<WordImage[]> {
    const { data, error } = await supabase
      .from("word_image")
      .select("image")
      .eq("word", word);

    if (error) {
      throw new Error(error.message);
    }

    return data.map((item) => ({
      id: item.image as number,
      url: `${SupabaseWordService.IMAGE_BASE_URL}${item.image}.png`,
    }));
  }

  async getImagesForWords(
    words: string[]
  ): Promise<Record<string, WordImage[]>> {
    const { data, error } = await supabase
      .from("word_image")
      .select("word, image")
      .in("word", words);

    if (error) {
      throw new Error(error.message);
    }

    const wordImages: Record<string, WordImage[]> = {};
    for (const item of data) {
      const word = item.word as string;
      const image = item.image as number;
      if (!wordImages[word]) {
        wordImages[word] = [];
      }
      wordImages[word].push({
        id: image,
        url: `${SupabaseWordService.IMAGE_BASE_URL}${image}.png`,
      });
    }

    return wordImages;
  }

  async getRandomWordImagesPairs(): Promise<WordImageUrlPair[]> {
    const { data, error } = await supabase.rpc("get_random_word_images", {
      p_limit: 3,
    });
    if (error || !data) return [];
    return data.map((row: { word: string; image: number }) => ({
      word: row.word,
      image_url: `${SupabaseWordService.IMAGE_BASE_URL}${row.image}.png`,
    }));
  }
}
